import React, { FC, useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { routes } from "@app/routing";
import {
  GisIntakeStep,
  STEP_ROUTE_SUFFIX,
  UpdatePrefilledDataInput,
  toNonNullFields,
} from "@app/service/intakeForm";
import { IntakeFormStepLayout, InlineCheckbox } from "../components";
import { useIntakeForm } from "../context";
import { useGisSessionOps } from "../useGisSessionOps";

const messages = defineMessages({
  title: {
    id: "intakeStepConsent.title",
    defaultMessage: "Finally, review and confirm information below:",
  },
  agreeToAll: {
    id: "intakeStepConsent.agreeToAll",
    defaultMessage: "Agree to all",
  },
  consentPatientPresent: {
    id: "intakeStepConsent.consentPatientPresent",
    defaultMessage:
      "I understand the patient who had the test performed must be present for the consult.",
  },
  consentEmailConfirmation: {
    id: "intakeStepConsent.consentEmailConfirmation",
    defaultMessage:
      "I understand I will be receiving an email confirmation for this appointment, and that if I need to reschedule or cancel my appointment, I must contact Natera at least 24 hours in advance by calling 650-489-9050.",
  },
  consentNoRecording: {
    id: "intakeStepConsent.consentNoRecording",
    defaultMessage:
      "I understand that Natera's policy is to not allow any recording (audio or video) of genetic information sessions.",
  },
  consentPrivacyPractices: {
    id: "intakeStepConsent.consentPrivacyPractices",
    defaultMessage:
      "I've received Natera's Notice of Privacy Practices (5/20/16) (www.natera.com/notice-privacy-practices). The NPP describes how Natera may use and disclose my protected health information and that Natera reserves the right to change such practices.",
  },
  submitButton: {
    id: "intakeStepConsent.submitButton",
    defaultMessage: "Submit",
  },
  submitError: {
    id: "intakeStepConsent.submitError",
    defaultMessage: "Unable to save your information. Please try again.",
  },
});

interface ConsentState {
  patientPresent: boolean;
  emailConfirmation: boolean;
  noRecording: boolean;
  privacyPractices: boolean;
}

const IntakeStepConsent: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { orderUidPublic, testUid } = useParams<{
    orderUidPublic: string;
    testUid: string;
  }>();
  const {
    prefilledData,
    userInputFields,
    updateUserField,
    setCurrentStep,
    setTriageGisFlowType,
    confirmedPhone,
    confirmedEmail,
    steps,
    totalSteps,
    getPreviousStep,
  } = useIntakeForm();

  const { getActiveSession, updateStep, completeSession } = useGisSessionOps();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const currentStep = GisIntakeStep.CONSENT;

  const previouslyAcknowledged = userInputFields.consentAcknowledged === true;
  const [consents, setConsents] = useState<ConsentState>({
    patientPresent: previouslyAcknowledged,
    emailConfirmation: previouslyAcknowledged,
    noRecording: previouslyAcknowledged,
    privacyPractices: previouslyAcknowledged,
  });

  useEffect(() => {
    setCurrentStep(currentStep);
  }, [setCurrentStep]);

  const allChecked = useMemo(() => {
    return (
      consents.patientPresent &&
      consents.emailConfirmation &&
      consents.noRecording &&
      consents.privacyPractices
    );
  }, [consents]);

  const handleAgreeToAll = (checked: boolean) => {
    setConsents({
      patientPresent: checked,
      emailConfirmation: checked,
      noRecording: checked,
      privacyPractices: checked,
    });
  };

  const handleConsentChange = (key: keyof ConsentState, checked: boolean) => {
    setConsents((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  const isContinueDisabled = () => {
    return !allChecked || isSubmitting;
  };

  const handleContinue = async () => {
    if (isContinueDisabled()) return;

    updateUserField("consentAcknowledged", true);
    setIsSubmitting(true);
    setSubmitError(false);

    try {
      const inputFieldsWithConsent = {
        ...userInputFields,
        consentAcknowledged: true,
      };

      // Look up the active triage row (created when the user clicked
      // "Schedule a GIS"), persist the user's intake answers, then mark
      // it COMPLETED. Completing the row runs triage rules server-side
      // and writes triage_result, which we use to drive routing.
      const activeSession = await getActiveSession({
        orderUidPublic: orderUidPublic || "",
        testUid: testUid || "",
      });

      const sessionId = activeSession?.id;
      if (sessionId == null) {
        throw new Error("No active GIS intake session found");
      }

      // Persist values collected into prefilledData by dedicated intake steps
      // so they survive server-side and are available to the Acuity booking.
      // Only send fields the active flow collects.
      const prefilledInput: UpdatePrefilledDataInput = {};
      if (prefilledData?.cancerType != null) {
        prefilledInput.cancerType = prefilledData.cancerType;
      }
      if (prefilledData?.cancerStage != null) {
        prefilledInput.cancerStage = prefilledData.cancerStage;
      }
      if (steps.includes(GisIntakeStep.CLINIC_INFO)) {
        if (prefilledData?.orderingPhysician) {
          prefilledInput.orderingPhysician =
            prefilledData.orderingPhysician.trim();
        }
        if (prefilledData?.physicianClinic) {
          prefilledInput.physicianClinic = prefilledData.physicianClinic.trim();
        }
      }
      const hasPrefilledInput = Object.keys(prefilledInput).length > 0;

      await updateStep({
        sessionId,
        input: toNonNullFields(inputFieldsWithConsent),
        confirmedPhone: confirmedPhone ?? undefined,
        confirmedEmail: confirmedEmail ?? undefined,
        prefilledInput: hasPrefilledInput ? prefilledInput : undefined,
      });

      const completed = await completeSession({ sessionId });
      const triageResult = completed?.triageResult;
      if (!triageResult) {
        throw new Error("Triage result missing from completed session");
      }

      if (triageResult.gisFlowType) {
        setTriageGisFlowType(triageResult.gisFlowType);
      }

      // Triage settles education on its own, including the no-order intake's
      // education page and the Empower-only exception to it.
      if (triageResult.skipPatientEducation ?? false) {
        navigate(
          routes.scheduleAGeneticInformationSessionSchedulePage(
            orderUidPublic || "",
            testUid || ""
          )
        );
      } else {
        navigate(
          routes.scheduleAGeneticInformationSessionPatientEducationPage(
            orderUidPublic || "",
            testUid || ""
          )
        );
      }
    } catch (err) {
      console.error("Failed to submit intake form:", err);
      setSubmitError(true);
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    const prevStep =
      getPreviousStep(currentStep) ?? GisIntakeStep.LOCATION_INTERPRETER;
    navigate(
      routes.scheduleAGeneticInformationSessionIntakePage(
        orderUidPublic || "",
        testUid || ""
      ) + STEP_ROUTE_SUFFIX[prevStep]
    );
  };

  return (
    <IntakeFormStepLayout
      currentStep={currentStep}
      totalSteps={totalSteps}
      title={intl.formatMessage(messages.title)}
      onContinue={handleContinue}
      onBack={handleBack}
      continueDisabled={isContinueDisabled()}
      continueLabel={intl.formatMessage(messages.submitButton)}
    >
      <div className="intake-form-step__consent-list">
        <InlineCheckbox
          id="consent-agree-all"
          label={intl.formatMessage(messages.agreeToAll)}
          checked={allChecked}
          onChange={handleAgreeToAll}
        />

        <InlineCheckbox
          id="consent-patient-present"
          label={intl.formatMessage(messages.consentPatientPresent)}
          checked={consents.patientPresent}
          onChange={(checked) => handleConsentChange("patientPresent", checked)}
        />

        <InlineCheckbox
          id="consent-email-confirmation"
          label={intl.formatMessage(messages.consentEmailConfirmation)}
          checked={consents.emailConfirmation}
          onChange={(checked) =>
            handleConsentChange("emailConfirmation", checked)
          }
        />

        <InlineCheckbox
          id="consent-no-recording"
          label={intl.formatMessage(messages.consentNoRecording)}
          checked={consents.noRecording}
          onChange={(checked) => handleConsentChange("noRecording", checked)}
        />

        <InlineCheckbox
          id="consent-privacy-practices"
          label={intl.formatMessage(messages.consentPrivacyPractices)}
          checked={consents.privacyPractices}
          onChange={(checked) =>
            handleConsentChange("privacyPractices", checked)
          }
        />
      </div>
      {submitError && (
        <p className="intake-form-step__error">
          {intl.formatMessage(messages.submitError)}
        </p>
      )}
    </IntakeFormStepLayout>
  );
};

export default IntakeStepConsent;
